import { formatISO } from 'date-fns';
import { purchaseDetailsTypes, topupFlows } from '@common/config/const';
import { analyticsService } from '@common/analytics';
import { getPricingOptions } from '@common/api/api';
import {
  mbwChat, TypingStatus
} from '@barges/mbw-chat';
import { refreshReducersFromResponse } from '../config/api_functions';
import Const from '../config/const';
import {
  toCamelCase, toSnakeCase, getErrorMessage, noInternetConnectionError
} from '../../common/config/utils';
import {
  StatusSuccess, StatusNoAnswer
} from './analytics';
import { getUser } from './user';
import { log, LOG_LEVELS } from '../../common/config/app_logger';
import { loadAdvisor } from './advisors';
import {
  cancelChat, pricingLoading, cancelChatOnPPActions,
  trackChatStartedEvent
} from './chat';
import { getAdvisorSlug } from '../project_items/helper_functions';
import { updatePurchaseDetails } from './purchase_details';
import { updateFxRate } from './application';
import { getUuidFromChatConfig } from '../config/util';

const NO_ANSWER = 'no_answer';
const ADVISOR_CONNECTION_LOST = 'advisor_connection_lost';

const updatePricingOptionsActionCreator = (pricingOptions, selectDurationKind, newUserPaygPricingOption, xfmProgram) => ({
  type: Const.chat.updatePricingOptions,
  payload: {
    pricingOptions, selectDurationKind, newUserPaygPricingOption, xfmProgram
  }
});

const trackMPChatStartedEventIfNeeded = (chatState, details, dispatch, getState) => {
  if (chatState !== Const.chatStates.started) return;

  const { chat, user: { user } } = getState();
  const { eligibleForQuickTopups, topupFlow } = details;
  const chatData = { ...chat, analytics: { ...chat.analytics, 'customer eligible for quick topups': eligibleForQuickTopups, 'top up flow': topupFlow } };
  trackChatStartedEvent(Const.chatType.text, StatusSuccess, chatData, user, dispatch);
  analyticsService.trackPageVisit('live session page');
};

const trackNoAnswerEventIfNeeded = (reason, dispatch, getState) => {
  if (reason !== NO_ANSWER) return;

  const { chat, user: { user } } = getState();
  trackChatStartedEvent(Const.chatType.text, StatusNoAnswer, chat, user, dispatch);
};

const updateUserIfNeeded = (state, dispatch) => {
  if (!(state === Const.chatStates.ended || state === Const.chatStates.paused)) return;

  dispatch(getUser());
};

const updateAdvisorIfEnded = (state, dispatch, getState) => {
  if (state !== Const.chatStates.ended) return;

  const { chat: { advisorId } } = getState();
  if (advisorId === null) return;
  dispatch(loadAdvisor(getAdvisorSlug({ advisorId })));
};

export const updatePricingOptions = (onContinue, dispatch, getState) => {
  dispatch(pricingLoading());
  const { chat: { advisorId } } = getState();
  const additionalParams = onContinue ? { context: 'followup' } : {};
  getPricingOptions(advisorId, { liveModes: 'chat', ...additionalParams }).then((res) => {
    if (res.fxRate != null) dispatch(updateFxRate(res.fxRate));
    dispatch(updatePricingOptionsActionCreator(res.chatPricingOptions || [], res.selectDurationKind, res.newUserPaygPricingOption, res.user?.xfmProgram));
  }).catch((error) => {
    log(LOG_LEVELS.ERROR, 'Connected Chat', 'Error updating pricing options', error);
    getErrorMessage(error).then(errorMessage => {
      if (errorMessage === noInternetConnectionError) return;
      dispatch({ type: Const.chat.cantCreate, errors: [errorMessage] });
    });
  });
};

const updatePricingOptionsOnContinueIfNeed = (state, dispatch, getState) => {
  if (state !== Const.chatStates.paused) return;
  updatePricingOptions(true, dispatch, getState);
};

export const cancelChatOnPP = (isContinue) => (dispatch, getState) => {
  updatePricingOptions(isContinue, dispatch, getState);
  dispatch(cancelChatOnPPActions(isContinue));
};

const updateActions = (state, dispatch, getState) => {
  updateAdvisorIfEnded(state, dispatch, getState);
  updateUserIfNeeded(state, dispatch);
  updatePricingOptionsOnContinueIfNeed(state, dispatch, getState);
};

const processTopupAdminMessage = (details, dispatch) => {
  refreshReducersFromResponse(details);
  dispatch(updatePurchaseDetails({
    clickSource: 'live chat',
    purchaseType: purchaseDetailsTypes.CHAT,
    topupFlow: topupFlows.quick,
    details: {
      duration: details.duration * 60,
      price: parseFloat(details.amount)
    },
    successCallback: () => {
      dispatch({
        type: Const.topup.show,
        ...details
      });
    }
  }));
};

const dispatchAdminMessage = ({ state, reason, ...details }, dispatch, getState) => {
  const { chat: { clientSummaryScreen } } = getState();
  if (state === Const.chatStates.ended && !!clientSummaryScreen) return;

  details = toCamelCase(details);

  trackMPChatStartedEventIfNeeded(state, details, dispatch, getState);
  trackNoAnswerEventIfNeeded(reason, dispatch, getState);

  updateActions(state, dispatch, getState);

  log(LOG_LEVELS.INFO, 'State', `state: ${ state }, reason: ${ reason }`);
  if (reason === NO_ANSWER) {
    dispatch({ type: Const.chat.noAnswer });
    return;
  }

  const chatState = getState().chat.state;
  if (chatState === null || (state === chatState && ![Const.chatStates.ended, Const.chatStates.started].includes(state))) return;
  // Close chat without complete screen if user hangup before the advisor answered
  if (
    (chatState === Const.chatStates.appConnectProvider
      || chatState === Const.chatStates.ringing
    ) && state === Const.chatStates.ended) {
    dispatch(cancelChat());
    return;
  }
  if (state === Const.chatStates.topup) {
    processTopupAdminMessage(details, dispatch);
    return;
  }
  dispatch({
    type: Const.chat.adminUpdate, state, details, reason
  });
};

const dispatchUserMessage = ({
  body, kind, reason, uuid, arrayIndex
}, timetoken, dispatch, messageId) => {
  if (reason === Const.message.status.offlineMessage) {
    dispatch({ type: Const.chat.messageUpdate, message: { timetoken, arrayIndex } });
    return;
  }

  if (kind === Const.message.kinds.text || kind === Const.message.kinds.textSystemMessage) {
    dispatch({
      type: Const.chat.message, message: {
        body, uuid: parseInt(uuid, 10), timetoken, kind, messageId
      }
    });
    return;
  }

  if (kind !== Const.message.kinds.notification) {
    log(LOG_LEVELS.ERROR, 'PubNub', `unknown kind: ${ kind }, reason: ${ reason }`);
  }
};

const connectPubNub = () => {
  mbwChat.subscribe({ withPresence: true });
};

const saveStorePubNubMessage = (message) => (dispatch) => {
  dispatch({ type: Const.chat.message, message });
};

const doSendMessage = ({
  kind, reason, body, arrayIndex
}) => (dispatch, getState) => {
  const {
    pubnub: { ownUuid, state: pubnubState } // need be chaked!!!!
  } = getState();
  const uuid = parseInt(ownUuid, 10);

  if (pubnubState === Const.pubnub.states.down) {
    if (kind === Const.message.kinds.text) {
      dispatch(saveStorePubNubMessage({
        body,
        uuid,
        offline: true,
        arrayIndex: getState().chat.messages.length
      }));
      log(LOG_LEVELS.INFO, 'PubNub', `saveStorePubNubMessage kind: ${ kind }, reason: ${ reason }`);
    }
    return;
  }

  const messagePayload = {
    user: {
      kind,
      reason,
      body: (body && body.trim()) || '',
      uuid
    }
  };
  if (reason === Const.message.status.offlineMessage && kind === Const.message.kinds.text) {
    messagePayload.user.arrayIndex = arrayIndex;
  }

  log(LOG_LEVELS.INFO, 'PubNub', `pubnub kind: ${ kind }, reason: ${ reason }`);
  mbwChat.publish({
    message: messagePayload,
    sendByPost: true,
    storeInHistory: kind === Const.message.kinds.text
  });
};

const sendStorePubNubMessage = (dispatch, getState) => {
  const {
    chat: { messages }
  } = getState();
  messages.forEach((m) => {
    if (m.offline) {
      log(LOG_LEVELS.INFO, 'PubNub', `sendStorePubNubMessage kind: ${ Const.message.kinds.text }, reason: ${ Const.message.status.offlineMessage }`);
      dispatch(doSendMessage({
        kind: Const.message.kinds.text,
        body: m.body,
        reason: Const.message.status.offlineMessage,
        arrayIndex: m.arrayIndex
      }));
    }
  });
};

export const setAadvisorConnectionLost = (dispatch) => {
  dispatch({ type: Const.chat.advisorConnectionLost });
};

export const clearAdvisorConnectionLost = () => dispatch => {
  dispatch({ type: Const.chat.clearAdvisorConnectionLost });
};

/** mbwChat onMessage: receives only message payload (no publisher/timetoken). Uses message.timestamp for time. */
const createMbwChatMessageHandler = (dispatch, getState) => (message) => {
  const { pubnub: { ownUuid } } = getState();
  const ts = message.timestamp ?? message.message?.timestamp ?? Date.now();
  const timetokenFormatted = formatISO(new Date(ts));

  if (message.timestamp != null) {
    dispatch({ type: Const.pubnub.actions.timetoken, timetoken: message.timestamp });
  }

  if (message.admin) {
    const { peers } = getState().chat;
    const publisher = message.user?.uuid ?? null;
    if (!peers || publisher == null || peers.findIndex(p => p.uuid === publisher) === -1) {
      dispatchAdminMessage(message.admin, dispatch, getState);
    }
    if (message.admin.reason === ADVISOR_CONNECTION_LOST) {
      setAadvisorConnectionLost(dispatch);
    }
    return;
  }

  if (message.sm && message.sm.receiver_ids?.includes(parseInt(ownUuid, 10))) {
    dispatchUserMessage(message.sm, timetokenFormatted, dispatch, message.message_id);
  }
  if (message.user) {
    dispatchUserMessage(message.user, timetokenFormatted, dispatch, message.message_id);
  }
};

/** mbwChat onStatus: receives { status: 'connected' | 'networkUp' | 'networkDown' }. */
const createMbwChatStatusHandler = (dispatch, getState) => (event) => {
  const status = event?.status;

  const actionMap = {
    connected: Const.pubnub.actions.connected,
    networkDown: Const.pubnub.actions.down,
    networkUp: Const.pubnub.actions.up
  };
  const type = actionMap[status];
  if (type) {
    dispatch({ type });
  }

  if (status === 'connected') { // networkUp is not needed because mbwChat will automatically reconnect
    sendStorePubNubMessage(dispatch, getState);
  }
};

export const connectChatProvider = () => (dispatch, getState) => {
  const { chat } = getState();
  const configForMbw = toSnakeCase(chat);
  const mbwChatLog = (message, data) => log(LOG_LEVELS.DEBUG, 'MbwChat', message, data ?? {});
  mbwChat.init(configForMbw, mbwChatLog);

  mbwChat.addListener({
    onMessage: createMbwChatMessageHandler(dispatch, getState),
    onStatus: createMbwChatStatusHandler(dispatch, getState),
    onPresence: (presence) => {
      log(LOG_LEVELS.DEBUG, 'PubNub', 'presence', presence);
    }
  });

  mbwChat.registerTypingCallback((props) => {
    const { status, uuid } = props;
    if (status === TypingStatus.STOPPED) {
      dispatch({ type: Const.chat.stopPeerTyping, uuid });
      return;
    }

    if (status === TypingStatus.STARTED) dispatch({ type: Const.chat.startPeerTyping, uuid });
  });

  connectPubNub();

  const uuid = getUuidFromChatConfig(chat);
  dispatch({ type: Const.pubnub.actions.connect, ownUuid: uuid });
};

export const disconnectChatProvider = () => (dispatch) => {
  if (mbwChat.isConnected()) {
    let mbwChatAnalytics = null;
    try {
      mbwChatAnalytics = mbwChat.getAnalyticsToMixpanel?.();
      if (mbwChatAnalytics && typeof mbwChatAnalytics === 'object' && Object.keys(mbwChatAnalytics).length) {
      // Optional: send to analytics, e.g. analyticsService.track('chat_session', analytics);
      }
    } catch (_) {
    // ignore
    }
    mbwChat.disconnect();
    dispatch({ type: Const.pubnub.actions.shutdown });
    return mbwChatAnalytics;
  }
  return null;
};

export const sendMessage = body => doSendMessage({ kind: Const.message.kinds.text, body });

export const sendTyping = () => {
  mbwChat.sendTyping();
};

export const sendStopTyping = () => {
  // mbwChat sends stop_typing automatically via typing strategy timeout; no-op here
};
